'use client';

import type { MouseEvent, ReactNode } from 'react';
import { cn, formatPrice } from '../lib/utils';
import { SanityImage } from '../sanity/utils/components';
import type { SanityImageProps } from '../sanity/utils/fragments/image-fragment';
import { useWishlistMutations } from '../wishlist/client';
import { CustomLink } from './custom-link';

export interface SharedWishlistCardData {
  _id: string;
  title: string | null;
  slug: string | null;
  sku: string | null;
  size: string | null;
  squareMetersPerBox: number | null;
  price: number | null;
  compareAtPrice: number | null;
  brand: {
    name: string | null;
    link: {
      slug: string;
    } | null;
  } | null;
  productGid: string | null;
  variantGid: string | null;
  mainImage: SanityImageProps | null;
}

export interface WishlistCardItem {
  id: string;
  productId: number;
  variantId: number;
  quantity: number;
}

function WishlistCardRoot({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <article
      className={cn(
        'relative isolate flex items-stretch space-y-1 overflow-hidden rounded-theme-lg text-brand-burgundy',
        className
      )}
    >
      {children}
    </article>
  );
}

function WishlistCardImageContainer({
  product,
  children,
  className,
}: {
  product: SharedWishlistCardData;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('relative flex-shrink-0', className)}>
      {product.slug && (
        <CustomLink
          href={`/products/${product.slug}`}
          className="relative block"
        >
          {product.mainImage && (
            <SanityImage
              image={product.mainImage}
              className="h-full w-full select-none object-cover"
              sizes="(max-width: 768px) 25vw, 20vw"
            />
          )}
        </CustomLink>
      )}
      {children}
    </div>
  );
}

function WishlistCardContent({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-full flex-1 flex-col items-stretch justify-between p-4',
        className
      )}
    >
      {children}
    </div>
  );
}

function WishlistCardHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('grid gap-1', className)}>{children}</div>;
}

function WishlistCardTitle({
  product,
  className,
}: {
  product: SharedWishlistCardData;
  className?: string;
}) {
  const title = product.title || 'Untitled Product';

  if (product.slug) {
    return (
      <CustomLink
        href={`/products/${product.slug}`}
        className={cn(
          'font-bold text-primary hf:underline hf:outline-none outline-none',
          className
        )}
      >
        {title}
      </CustomLink>
    );
  }

  return (
    <span className={cn('font-bold text-primary', className)}>{title}</span>
  );
}

function WishlistCardBrand({
  product,
  className,
}: {
  product: SharedWishlistCardData;
  className?: string;
}) {
  if (!product.brand?.name) return null;

  const content = (
    <span className={cn('text-gray-600 text-sm', className)}>
      {product.brand.name}
    </span>
  );

  if (product.brand.link?.slug) {
    return (
      <CustomLink href={`/people/${product.brand.link.slug}`}>
        {content}
      </CustomLink>
    );
  }

  return content;
}

function WishlistCardFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center justify-between', className)}>
      {children}
    </div>
  );
}

function WishlistCardPrice({
  product,
  containerClassName,
  priceClassName,
  compareAtPriceClassName,
}: {
  product: SharedWishlistCardData;
  containerClassName?: string;
  priceClassName?: string;
  compareAtPriceClassName?: string;
}) {
  if (!product.price) {
    return (
      <div className={cn('text-gray-500', containerClassName)}>
        Price unavailable
      </div>
    );
  }

  const correctCompareAtPrice =
    product.compareAtPrice !== 0 ? product.compareAtPrice : null;

  const squareMetersPerBox = product.squareMetersPerBox || 1;
  const isTile = squareMetersPerBox > 1;
  const unit = isTile ? '/ m²' : '';

  const displayPrice = isTile
    ? product.price / squareMetersPerBox
    : product.price;
  const displayCompareAtPrice =
    correctCompareAtPrice && isTile
      ? correctCompareAtPrice / squareMetersPerBox
      : correctCompareAtPrice;

  return (
    <div
      className={cn(
        'flex items-baseline justify-end gap-1.5',
        containerClassName
      )}
    >
      <div className="flex items-baseline gap-1.5">
        {displayCompareAtPrice && displayCompareAtPrice > displayPrice && (
          <span
            className={cn(
              'font-normal text-gray-500 line-through',
              compareAtPriceClassName
            )}
          >
            <b>{formatPrice(displayCompareAtPrice)}</b>
          </span>
        )}
        <span className={cn('font-normal', priceClassName)}>
          <b>{formatPrice(displayPrice)}</b>
          {unit && <> {unit}</>}
        </span>
      </div>
    </div>
  );
}

function WishlistCardSize({
  product,
  className,
}: {
  product: SharedWishlistCardData;
  className?: string;
}) {
  if (!product.size) return null;

  return (
    <span className={cn('text-gray-600 text-sm', className)}>
      <b className="text-[hsl(var(--color))]">{product.size}</b> mm
    </span>
  );
}

function WishlistCardRemoveButton({
  item,
  boardId,
  className,
  children,
}: {
  item: WishlistCardItem;
  boardId?: string;
  className?: string;
  children?: ReactNode;
}) {
  const { removeItemMutation, removeItemFromBoardMutation } =
    useWishlistMutations();

  const handleRemove = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // If boardId is provided, remove from specific board only
    // If no boardId, remove item completely (from All Items)
    if (boardId) {
      removeItemFromBoardMutation.mutate({ itemId: item.id, boardId });
    } else {
      removeItemMutation.mutate(item.id);
    }
  };

  const isLoading = boardId
    ? removeItemFromBoardMutation.isPending
    : removeItemMutation.isPending;

  return (
    <button
      type="button"
      onClick={handleRemove}
      disabled={isLoading}
      className={cn(
        'text-gray-500 transition-colors hover:text-red-600',
        isLoading && 'cursor-not-allowed opacity-50',
        className
      )}
      aria-label={`Remove ${item.productId} from wishlist`}
    >
      {children || (
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <title>Remove Item</title>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      )}
    </button>
  );
}

// Compound component structure
WishlistCardRoot.ImageContainer = WishlistCardImageContainer;
WishlistCardRoot.Content = WishlistCardContent;
WishlistCardRoot.Header = WishlistCardHeader;
WishlistCardRoot.Title = WishlistCardTitle;
WishlistCardRoot.Brand = WishlistCardBrand;
WishlistCardRoot.Footer = WishlistCardFooter;
WishlistCardRoot.Price = WishlistCardPrice;
WishlistCardRoot.Size = WishlistCardSize;
WishlistCardRoot.RemoveButton = WishlistCardRemoveButton;

export const WishlistCard = WishlistCardRoot;
